import React from 'react';
import styles from './DeployNightlyDrawer.module.css';
import Ico from '../common/Ico';
import Pill from '../common/Pill';
import TierPill from '../common/TierPill';
import { avatarColor, clusterInitial, flagFor } from '../../helpers/clusters';
import { canDeployOldNightly } from '../../helpers/roles';
import { statusLabel } from './statusConfig';

const ICO_LAYERS = 'm12 2 10 6-10 6L2 8l10-6z M2 16l10 6 10-6 M2 12l10 6 10-6';
const ICO_X       = 'M18 6 6 18M6 6l12 12';
const ICO_CHECK   = 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11';
const ICO_FILTER  = 'M22 3H2l8 9.46V19l4 2v-8.54L22 3z';
const ICO_SEARCH  = 'M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z';
const ICO_PLUS    = 'M12 5v14M5 12h14';
const ICO_PLAY    = 'm5 3 14 9-14 9V3z';
const ICO_ALERT   = 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01';
const DEPLOYABLE_CLUSTER_STATUSES = new Set(['running', 'modifying']);

function ClusterAvatar({ name, size = 30 }) {
  return (
    <span
      className={styles.avatar}
      style={{ background: avatarColor(name), width: size, height: size, fontSize: Math.round(size * 0.37) }}
    >
      {clusterInitial(name)}
    </span>
  );
}

function clusterMeta(c) {
  const parts = [];
  if (c.region) parts.push(`${flagFor(c.region)} ${c.region}`);
  if (c.version) parts.push(`v${c.version}`);
  return parts.join(' · ');
}

function versionsMatch(clusterVersion, branchVersion) {
  if (!clusterVersion || !branchVersion) return false;
  const cv = String(clusterVersion).replace(/^v/i, '');
  const bv = String(branchVersion).replace(/^v/i, '');
  return cv === bv || cv.startsWith(`${bv}.`) || cv.startsWith(`${bv}-`);
}

function isOldNightly(run, latestRunNumber) {
  if (!run || latestRunNumber == null) return false;
  return latestRunNumber - run.number >= 3;
}

function rolloutClusterIds(run) {
  return (run?.deployments || []).map((entry) => entry.cluster_id).filter(Boolean);
}

function rolloutEntries(run) {
  return run?.deployments || [];
}

function hasRollout(run) {
  return (
    run?.rollout_created === true ||
    run?.rollout_created === 'true' ||
    rolloutClusterIds(run).length > 0
  );
}

function isDeployableCluster(c) {
  return DEPLOYABLE_CLUSTER_STATUSES.has((c.status || '').toLowerCase());
}

export default function DeployNightlyDrawer({
  open,
  candidateOnly = false,
  onClose,
  onConfirm,
  run,
  latestRunNumber = null,
  branch,
  user,
  allClusters = [],
  clustersLoading = false,
}) {
  const branchVersion = branch?.version;
  const isOld = isOldNightly(run, latestRunNumber);
  const canOverrideOldNightly = canDeployOldNightly(user);
  const buildCompleted = run?.result?.build_docker?.status === 'completed';
  const hasBuildTag = Boolean(run?.build_tag);
  const rolloutCreated = hasRollout(run);
  const canDeploy = candidateOnly || (buildCompleted && hasBuildTag && (!isOld || canOverrideOldNightly));
  const existingRolloutIds = rolloutClusterIds(run);
  const existingRolloutIdSet = new Set(existingRolloutIds);
  const allClusterById = new Map(allClusters.map((c) => [c.id, c]));
  const existingRolloutClusters = rolloutEntries(run).map((entry) => ({
    ...(allClusterById.get(entry.cluster_id) || {}),
    ...entry,
    id: entry.cluster_id,
    name: allClusterById.get(entry.cluster_id)?.name || entry.cluster_id,
  }));

  const [pinned,      setPinned]      = React.useState([]);
  const [q,           setQ]           = React.useState('');
  const [addOpen,     setAddOpen]     = React.useState(false);
  const [addQ,        setAddQ]        = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setQ('');
    setAddOpen(false);
    setAddQ('');
    setPinned((branch?.build_candidates || []).filter((id) => !existingRolloutIdSet.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  const isInFilter = (c) =>
    (c.tier || '').toLowerCase() === 'trial' && versionsMatch(c.version, branchVersion);

  const selectableClusters = allClusters
    .filter(isDeployableCluster)
    .filter((c) => !existingRolloutIdSet.has(c.id));
  const pinnedSet      = new Set(pinned);
  const clusterById    = new Map(selectableClusters.map((c) => [c.id, c]));
  const candidates     = pinned.map((id) => clusterById.get(id)).filter(Boolean);
  const selectedCandidateIds = candidates.map((c) => c.id);
  const matching       = selectableClusters.filter((c) => isInFilter(c) && !pinnedSet.has(c.id));
  const pool           = selectableClusters.filter((c) => !isInFilter(c) && !pinnedSet.has(c.id));
  const visibleMatching = matching.filter((c) =>
    !q || (c.name || c.id || '').toLowerCase().includes(q.toLowerCase())
  );
  const visiblePool    = pool.filter((c) =>
    !addQ || (c.name || c.id || '').toLowerCase().includes(addQ.toLowerCase())
  );

  const pin    = (id) => setPinned((prev) => prev.includes(id) ? prev : [...prev, id]);
  const pinAll = ()   => setPinned((prev) => {
    const toAdd = matching.map((c) => c.id).filter((id) => !prev.includes(id));
    return [...prev, ...toAdd];
  });
  const unpin  = (id) => setPinned((prev) => prev.filter((x) => x !== id));

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.headerIcon}>
            <Ico d={ICO_LAYERS} size={19} strokeWidth={1.7} />
          </span>
          <div className={styles.headerText}>
            <div className={styles.title}>
              {candidateOnly ? 'Edit build candidates' : rolloutCreated ? 'Add rollout clusters' : 'Deploy nightly build'}
            </div>
            <div className={styles.subtitle}>
              {candidateOnly ? (
                <>
                  Choose clusters saved to branch <code className={styles.buildCode}>{branchVersion}</code>
                </>
              ) : (
                <>
                  Choose where nightly <code className={styles.buildCode}>{run ? `#${run.number}` : ''}</code> rolls out
                </>
              )}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
            <Ico d={ICO_X} size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className={styles.body}>

          {!candidateOnly && isOld && (
            <div className={canOverrideOldNightly ? styles.oldNightlyWarning : styles.oldNightlyBlocked}>
              <Ico
                d={ICO_ALERT}
                size={14}
                style={{
                  color: canOverrideOldNightly ? 'var(--warning-600)' : 'var(--error-600)',
                  flexShrink: 0,
                }}
              />
              <span>
                You are deploying an older nightly build. Only the latest 3 nightlies are open for regular deploys.
                {!canOverrideOldNightly && ' Support or DevOps members can override this restriction.'}
              </span>
            </div>
          )}

          {!candidateOnly && rolloutCreated && (
            <>
              <div className={styles.candidatesHead}>
                <div className={styles.sectionHeaderRow}>
                  <Ico d={ICO_LAYERS} size={14} style={{ color: '#1570EF', flexShrink: 0 }} strokeWidth={1.8} />
                  <span className={styles.sectionLabel}>Already in rollout</span>
                  <span className={styles.countBadge}>{existingRolloutClusters.length}</span>
                </div>
              </div>

              {existingRolloutClusters.map((c) => {
                const outside = !isInFilter(c);
                return (
                  <div key={c.id} className={`${styles.candidateCard} ${styles.lockedCandidateCard}`}>
                    <ClusterAvatar name={c.name || c.id} size={30} />
                    <div className={styles.clusterInfo}>
                      <div className={styles.clusterNameRow}>
                        <span className={styles.clusterName}>{c.name || c.id}</span>
                        {c.tier && <TierPill tier={c.tier} />}
                        {outside && <span className={styles.outsideBadge}>Outside filter</span>}
                      </div>
                      <div className={styles.clusterMeta}>{clusterMeta(c)}</div>
                    </div>
                    <Pill status={c.status} dot={false}>{statusLabel(c.status)}</Pill>
                  </div>
                );
              })}
            </>
          )}

          {/* BUILD CANDIDATES */}
          <div className={styles.candidatesHead}>
            <div className={styles.sectionHeaderRow}>
              <Ico d={ICO_CHECK} size={14} style={{ color: '#1570EF', flexShrink: 0 }} strokeWidth={1.8} />
              <span className={styles.sectionLabel}>
                {candidateOnly ? 'Build candidates' : rolloutCreated ? 'Add clusters' : 'Build candidates'}
              </span>
              <span className={styles.countBadge}>{candidates.length}</span>
            </div>
            <div className={styles.candidatesDesc}>
              {candidateOnly
                ? 'Saved to the branch for manual nightly deploys and automatic rollout.'
                : 'These roll out immediately — saved to branch '}
              {!candidateOnly && <strong>{branchVersion}</strong>}
            </div>
          </div>

          {candidates.length === 0 ? (
            <div className={styles.candidatesEmpty}>
              No candidates yet — pin clusters below to save them to the branch.
            </div>
          ) : (
            candidates.map((c) => {
              const outside = !isInFilter(c);
              return (
                <div key={c.id} className={styles.candidateCard}>
                  <ClusterAvatar name={c.name || c.id} size={30} />
                  <div className={styles.clusterInfo}>
                    <div className={styles.clusterNameRow}>
                      <span className={styles.clusterName}>{c.name || c.id}</span>
                      {c.tier && <TierPill tier={c.tier} />}
                      {outside && <span className={styles.outsideBadge}>Outside filter</span>}
                    </div>
                    <div className={styles.clusterMeta}>{clusterMeta(c)}</div>
                  </div>
                  <button className={styles.removeBtn} type="button" onClick={() => unpin(c.id)} title="Remove">
                    <Ico d={ICO_X} size={13} strokeWidth={1.9} />
                  </button>
                </div>
              );
            })
          )}

          {/* MATCHING CLUSTERS */}
          <div className={styles.matchingSection}>
            <div className={styles.sectionHeaderRow}>
              <Ico d={ICO_FILTER} size={13} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} strokeWidth={1.8} />
              <span className={styles.sectionLabel}>Matching clusters</span>
              <span className={styles.matchingMeta}>
                · Trial on <code className={styles.mono}>{branchVersion}.x</code>
              </span>
              {matching.length > 0 && (
                <button className={styles.pinAllBtn} onClick={pinAll} type="button">
                  <Ico d={ICO_CHECK} size={12} strokeWidth={2} />
                  Pin all
                </button>
              )}
            </div>
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon}><Ico d={ICO_SEARCH} size={14} /></span>
              <input
                className={styles.searchInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search matching clusters…"
              />
            </div>
          </div>

          <div className={styles.matchingList}>
            {clustersLoading ? (
              <div className={styles.emptyMsg}>Loading clusters…</div>
            ) : visibleMatching.length === 0 ? (
              <div className={styles.emptyMsg}>
                {matching.length === 0
                  ? 'All matching clusters are already pinned or in the rollout.'
                  : 'No clusters match the search.'}
              </div>
            ) : (
              visibleMatching.map((c) => (
                <div key={c.id} className={styles.matchingRow}>
                  <ClusterAvatar name={c.name || c.id} size={30} />
                  <div className={styles.clusterInfo}>
                    <div className={styles.clusterNameRow}>
                      <span className={styles.clusterName}>{c.name || c.id}</span>
                      {c.tier && <TierPill tier={c.tier} />}
                    </div>
                    <div className={styles.clusterMeta}>{clusterMeta(c)}</div>
                  </div>
                  <button className={styles.pinBtn} type="button" onClick={() => pin(c.id)}>
                    <Ico d={ICO_PLUS} size={13} strokeWidth={2} />
                    Pin
                  </button>
                </div>
              ))
            )}
          </div>

          <div className={styles.addOutsideWrap}>
            <button
              className={styles.addOutsideBtn}
              type="button"
              onClick={() => { setAddOpen((o) => !o); setAddQ(''); }}
            >
              <Ico d={ICO_PLUS} size={14} strokeWidth={1.8} />
              Add a cluster outside the filter
            </button>

            {addOpen && (
              <div className={styles.addDropdownPanel}>
                <div className={styles.addDropdownSearch}>
                  <span className={styles.addSearchIcon}><Ico d={ICO_SEARCH} size={13} /></span>
                  <input
                    autoFocus
                    className={styles.addDropdownInput}
                    value={addQ}
                    onChange={(e) => setAddQ(e.target.value)}
                    placeholder="Search all clusters…"
                  />
                </div>
                <div className={styles.addDropdownList}>
                  {visiblePool.length === 0 ? (
                    <div className={styles.emptyMsg} style={{ padding: '14px' }}>No clusters match.</div>
                  ) : (
                    visiblePool.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={styles.addDropdownRow}
                        onClick={() => { pin(c.id); setAddOpen(false); setAddQ(''); }}
                      >
                        <ClusterAvatar name={c.name || c.id} size={26} />
                        <div className={styles.clusterInfo}>
                          <div className={styles.clusterName}>{c.name || c.id}</div>
                          <div className={styles.clusterMeta}>{clusterMeta(c)}</div>
                        </div>
                        <Ico d={ICO_PLUS} size={14} style={{ color: '#1570EF', flexShrink: 0 }} strokeWidth={1.9} />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerNote}>
            {candidateOnly ? 'Saving' : 'Rolling out to'} <strong>{candidates.length}</strong> cluster{candidates.length !== 1 ? 's' : ''}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelBtn} onClick={onClose} type="button">Cancel</button>
            <button
              className={styles.confirmBtn}
              type="button"
              onClick={() => candidateOnly
                ? onConfirm(selectedCandidateIds)
                : onConfirm(
                  selectedCandidateIds,
                  [...existingRolloutIds, ...selectedCandidateIds],
                )}
              disabled={candidateOnly ? false : candidates.length === 0 || !canDeploy}
              title={!canDeploy ? 'This nightly cannot be deployed' : undefined}
            >
              <Ico d={ICO_PLAY} size={14} strokeWidth={1.8} />
              {candidateOnly ? 'Save candidates' : rolloutCreated ? 'Add clusters' : 'Deploy'}
            </button>
          </div>
        </div>

      </div>
    </>
  );
}
